<template>
    <div v-if="isPreview">
        <strong>{{ paymentsDisabledMessage }}</strong>
    </div>
    <pc-loader
        v-else
        id="PaymentPage"
        v-bind:loading="loading"
        v-bind:message="paymentInformationLoadingMessage">
        <template v-if="!loading">
            <div v-if="projectLockStore.lockedForPendingPayment">
                <div class="d-flex mb-3 align-items-center">
                    <pc-loader
                        small
                        no-bg
                        loading
                        class="epayment-loader-size" />
                    <h2 class="m-0">
                        {{ paymentConfirmationPendingHeader }}
                    </h2>
                </div>
                <p
                    v-if="pageInfo.PaymentPageLockedMessage1"
                    class="fw-bold">
                    {{ pageInfo.PaymentPageLockedMessage1 }}
                </p>
                <p
                    v-for="(message, index) in paymentPageLockSecondaryMessages"
                    v-bind:key="index">
                    {{ message }}
                </p>
                <br />
                <div class="d-flex flex-column align-items-center mb-2">
                    <div class="btn-row">
                        <!-- check this class-->
                        <pc-button
                            id="abandonPaymentButton"
                            primary
                            v-on:click="confirmCancelPending">
                            {{ abandonPaymentButtonLabel }}
                        </pc-button>
                    </div>
                </div>
                <div
                    v-if="hasPayments"
                    data-test-role="previous-payments">
                    <h3>{{ previousPaymentsTableHeader }}</h3>
                    <pc-table
                        v-bind:columns="receiptCols"
                        v-bind:items="pageInfo.PreviousPayments"
                        key-prop="ChargeId"
                        hide-search />
                </div>
            </div>
            <div v-else-if="!isConfiguredForPayment">
                <strong>{{ isNotConfiguredForPaymentMessage }}</strong>
            </div>
            <div v-else>
                <div v-if="!pageInfo.IsViewOnly">
                    <h2>{{ ePaymentPageHeader }}</h2>
                    <template v-if="hasUnsubmittedPayments">
                        <div>
                            {{ unsubmittedPaymentsMessage }}
                        </div>
                        <h3>{{ unsubmittedPaymentsTableHeader }}</h3>
                        <pc-table
                            v-bind:columns="receiptCols"
                            v-bind:items="pageInfo.UnsubmittedPayments"
                            key-prop="ChargeId"
                            hide-search />
                    </template>
                    <template v-else>
                        <div
                            v-if="hasMissingFields"
                            class="text-danger fw-bold">
                            {{ missingFieldsMessage }}
                        </div>
                        <div
                            v-if="hasAutomationFailures"
                            class="text-danger fw-bold">
                            {{ formValidationErrorsMessage }}
                        </div>
                        <div
                            v-if="hasChargeAmountError"
                            class="text-danger fw-bold">
                            <p>
                                {{ chargeAmountErrorMessage }}
                            </p>
                            <p>{{ errorLabel }} {{ pageInfo.ChargeAmountValidationError }}</p>
                            <p v-if="pageInfo.ChargeErrorAmountDisplay">
                                {{ amountErrorLabel }} {{ pageInfo.ChargeErrorAmountDisplay }}
                            </p>
                        </div>
                        <div v-if="!hasErrors">
                            <div v-if="pageInfo.ShowPayment">
                                <div v-if="pageInfo.WasProjectNumberGenerated">
                                    <strong>{{ projectNumberLabel }} {{ pageInfo.ProjectNumber }}</strong>
                                </div>
                                <div>
                                    <span>
                                        {{ chargeDescriptionLabel }}:
                                        <span data-test-role="amount-to-pay">
                                            {{ pageInfo.PaymentInfo.DisplayAmount }}
                                        </span>
                                    </span>
                                </div>
                                <div
                                    v-if="pageInfo.CaptureBillingEmail"
                                    class="form-element-container">
                                    <!--change class -->
                                    <pc-input
                                        ref="billingEmail"
                                        v-model.trim="pageInfo.PaymentInfo.Email"
                                        v-bind:validator="billingEmailValidator"
                                        v-bind:label="billingEmailLabel"
                                        data-test-role="billing-email-input"
                                        required
                                        immediate-validation
                                        v-on:validated="billingEmailValidated" />
                                    <pc-input
                                        v-if="showConfirmEmail"
                                        ref="confirmEmail"
                                        v-model.trim="confirmBillingEmail"
                                        v-bind:validator="confirmBillingEmailValidator"
                                        v-bind:label="confirmBillingEmailLabel"
                                        data-test-role="confirm-billing-email-input"
                                        required
                                        immediate-validation
                                        show-validation
                                        v-on:validated="confirmBillingEmailValidated" />
                                </div>
                                <div v-if="pageInfo.CustomMessage">
                                    <!-- eslint-disable-next-line vue/no-v-html -->
                                    <div v-html="pageInfo.CustomMessage" />
                                </div>
                                <div>
                                    <div
                                        v-if="displayEPaymentTypeTableHeader"
                                        class="mt-4">
                                        Payment method options:
                                    </div>
                                    <div
                                        v-if="displayEPaymentTypeTable"
                                        class="ms-4">
                                        <div
                                            v-for="option in ePaymentTypeOptions"
                                            v-bind:key="option.DisplayName"
                                            class="my-2">
                                            {{ option.DisplayName }}<br />
                                            <table class="ms-4">
                                                <tbody>
                                                    <tr>
                                                        <td style="text-align: left">Transaction Fee:</td>
                                                        <td style="text-align: right">
                                                            {{ option.SurchargeDisplayAmount }}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style="text-align: left">Total Amount:</td>
                                                        <td style="text-align: right">
                                                            {{ option.TotalDisplayAmount }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="mustSelectEPaymentType"
                                    class="d-flex align-items-end my-3">
                                    <pc-radio-group
                                        id="ePaymentTypeSelector"
                                        v-model="selectedEPaymentType"
                                        name="ePaymentTypeSelector"
                                        v-bind:disabled="isReadOnly || isPreview"
                                        v-bind:required="!pageInfo.AllowManualPayments"
                                        v-bind:label="ePaymentTypeLabel"
                                        v-bind:options="ePaymentTypeRadioOptions"
                                        class="me-3" />
                                </div>
                                <div class="mb-3">
                                    <pc-button
                                        v-bind:disabled="!ePayIsOk"
                                        data-test-role="pay-and-submit-btn"
                                        class="payment-button"
                                        v-on:click="processEPayment">
                                        {{ ePayButtonLabel }}
                                    </pc-button>
                                </div>
                                <div
                                    v-if="pageInfo.AllowManualPayments"
                                    class="mt-3 mb-5">
                                    Or make a manual payment:<br />
                                    <pc-button
                                        v-bind:disabled="!manualPayIsOk"
                                        data-test-role="outside-pay-btn"
                                        class="payment-button"
                                        v-on:click="processManualPayment">
                                        {{ outsidePayButtonLabel }}
                                    </pc-button>
                                </div>
                            </div>
                            <div v-if="!pageInfo.ShowPayment">
                                <span>{{ paymentNotRequiredMessage }}</span>
                            </div>
                        </div>
                    </template>
                </div>
                <div
                    v-if="hasPayments"
                    data-test-role="previous-payments">
                    <h3>{{ previousPaymentsTableHeader }}</h3>
                    <pc-table
                        v-bind:columns="receiptCols"
                        v-bind:items="pageInfo.PreviousPayments"
                        key-prop="ChargeId"
                        hide-search />
                </div>
            </div>
        </template>
    </pc-loader>
</template>
<script>
    import { FORM_VAL_ERR_TYPE } from "Forms/formValidationType";
    import { PROJECT_FORM_CONTROLLER_TYPES } from "./projectFormControllerStore";
    import { getValidator, VALIDATOR_TYPES } from "Validation/validator";
    import { PcxAdapter } from "./paymentAdapters/pcxAdapter";
    import { StripeAdapter } from "./paymentAdapters/stripeAdapter";
    import { EzPayAdapter } from "./paymentAdapters/ezPayAdapter";
    import { PaymentusAdapter } from "./paymentAdapters/paymentusAdapter";
    import paymentReceipt from "./paymentReceipt.vue";
    import { getClientTimestampTableColumn } from "Util/clientDateTime";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/ePaymentPage.i18n";
    import { useProjectLockStore } from "Projects/projectLockStore.js";

    export default {
        inject: ["projectForm", "baseController"],
        props: {
            programId: { type: String, required: true },
            projectId: { type: String, required: true },
            formVersionId: { type: String, required: true },
            useDraftStorage: { type: Boolean, required: true },
            isReadOnly: { type: Boolean, required: true },
            isPreview: { type: Boolean, required: true }
        },
        setup() {
            const projectLockStore = useProjectLockStore();
            return { projectLockStore };
        },
        data() {
            return {
                pageInfo: null,
                emailValidationResults: null,
                confirmEmailValidationResults: null,
                willPayAgain: false,
                receiptData: null,
                adapter: null,
                showConfirmEmail: false,
                confirmBillingEmail: null,
                selectedEPaymentType: ""
            };
        },
        computed: {
            receiptCols() {
                return [
                    getClientTimestampTableColumn(this.previousPaymentsDateColumnHeader, "ReceiptTimestamp"),
                    {
                        header: this.previousPaymentsAmountColumnHeader,
                        property: "Amount",
                        orderable: true,
                        visible: true
                    },
                    {
                        header: this.previousPaymentsPayerEmailColumnHeader,
                        property: "PayerEmail",
                        orderable: true,
                        visible: this.pageInfo.CaptureBillingEmail
                    },
                    {
                        header: this.previousPaymentsPayerNameColumnHeader,
                        property: "PayerName",
                        orderable: true,
                        visible: !this.pageInfo.CaptureBillingEmail
                    },
                    {
                        header: this.previousPaymentsReceiptNumberColumnHeader,
                        property: "ReceiptNumber",
                        orderable: true,
                        visible: true
                    }
                ];
            },
            loading() {
                return !this.pageInfo;
            },
            isConfiguredForPayment() {
                return this.pageInfo?.IsConfiguredForPayment || false;
            },
            displayEPaymentTypeTableHeader() {
                return this.ePaymentTypeOptions.length > 1 && this.pageInfo.IncludeTransactionFee;
            },
            displayEPaymentTypeTable() {
                return this.pageInfo.IncludeTransactionFee;
            },
            ePaymentTypeOptions() {
                let options = [];
                if (this.pageInfo && this.pageInfo.EPaymentTypeOptions) {
                    options = this.pageInfo.EPaymentTypeOptions;
                }
                return options;
            },
            ePaymentTypeRadioOptions() {
                return this.ePaymentTypeOptions.map((option) => {
                    let optionText = option.DisplayName;
                    if (this.pageInfo.IncludeTransactionFee) {
                        optionText += ": " + option.TotalDisplayAmount;
                    }
                    return {
                        value: option.ProviderMethodId,
                        text: optionText
                    };
                });
            },
            mustSelectEPaymentType() {
                return this.pageInfo.MustSelectPaymentType;
            },
            hasErrors() {
                return this.hasMissingFields || this.hasAutomationFailures || this.hasChargeAmountError;
            },
            hasMissingFields() {
                if (this.pageInfo && this.pageInfo.FormValidationErrors) {
                    return this.hasErrorType(this.pageInfo.FormValidationErrors, FORM_VAL_ERR_TYPE.ElementMissing);
                }
                return false;
            },
            hasAutomationFailures() {
                if (this.pageInfo && this.pageInfo.FormValidationErrors) {
                    return this.hasErrorType(this.pageInfo.FormValidationErrors, FORM_VAL_ERR_TYPE.AutomationFailure);
                }
                return false;
            },
            hasChargeAmountError() {
                return Boolean(this.pageInfo && this.pageInfo.ChargeAmountValidationError);
            },
            hasUnsubmittedPayments() {
                if (this.pageInfo) {
                    return this.pageInfo.UnsubmittedPayments.length > 0;
                }
                return false;
            },
            hasPayments() {
                if (this.pageInfo) {
                    return this.pageInfo.PreviousPayments.length > 0;
                }
                return false;
            },
            billingEmailValidator() {
                return getValidator(this.$app, [VALIDATOR_TYPES.email, VALIDATOR_TYPES.required]);
            },
            confirmBillingEmailValidator() {
                return getValidator(this.$app, [
                    VALIDATOR_TYPES.email,
                    VALIDATOR_TYPES.required,
                    {
                        type: VALIDATOR_TYPES.custom,
                        validate: this._doEmailsMatch,
                        errorMsg: this.confirmBillingEmailErrorMessage
                    }
                ]);
            },
            isEmailValid() {
                if (this.pageInfo) {
                    if (this.emailValidationResults) {
                        if (this.confirmEmailValidationResults && this.confirmEmailValidationResults.hasError) {
                            return false;
                        }
                        return !this.emailValidationResults.hasError;
                    }

                    return true;
                }
                return false;
            },
            readyToPay() {
                return this.isEmailValid && !this.hasErrors && !this.hasUnsubmittedPayments;
            },
            manualPayIsOk() {
                return this.readyToPay;
            },
            ePayIsOk() {
                return this.readyToPay && (!this.mustSelectEPaymentType || this.selectedEPaymentType);
            },
            chargeId() {
                if (this.pageInfo && this.pageInfo.PaymentInfo) {
                    return this.pageInfo.PaymentInfo.ChargeId;
                }
                return null;
            },
            displayChargeDefinitionDescriptionLabel() {
                // Intentionally displaying the ChargeDefinitionDescription label all the time
                return true;
            },
            chargeDescriptionLabel() {
                if (this.pageInfo && this.pageInfo.PaymentInfo) {
                    if (this.displayChargeDefinitionDescriptionLabel) {
                        return this.pageInfo.PaymentInfo.ChargeDescription;
                    }
                    return this.amountToPayLabel;
                }
                return null;
            },
            payerEmail() {
                if (this.pageInfo && this.pageInfo.PaymentInfo) {
                    return this.pageInfo.PaymentInfo.Email;
                }
                return null;
            },
            paymentPageLockSecondaryMessages() {
                const messages = [];
                if (this.pageInfo.PaymentPageLockedMessage2) {
                    messages.push(this.pageInfo.PaymentPageLockedMessage2);
                }
                if (this.pageInfo.PaymentPageLockedMessage3) {
                    messages.push(this.pageInfo.PaymentPageLockedMessage3);
                }
                if (this.pageInfo.PaymentPageLockedMessage4) {
                    messages.push(this.pageInfo.PaymentPageLockedMessage4);
                }
                return messages;
            },
            // Localization
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        async created() {
            this.projectForm.showSubmitOnEPaymentPage = false;
            if (this.isPreview) {
                return;
            }
            if (this.projectForm.beforeSubmit) {
                try {
                    await this.projectForm.beforeSubmit();
                } catch (error) {
                    return;
                }
            }

            this.projectForm.clearValidationErrors();
            await this.$store.dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.waitForActiveSaves, null, { root: true });

            const result = await this.$serviceControllers.EPayments.LoadInfo({
                programId: this.programId,
                projectId: this.projectId,
                formVersionId: this.formVersionId,
                useDraftStorage: this.useDraftStorage,
                isReadOnly: this.isReadOnly
            });

            // hold off on setting page info to this object until we know everything loaded correctly
            const pageInfo = result.Data;
            if (!this.projectLockStore.lockedForPendingPayment && pageInfo.ProjectIsLocked) {
                await this.$app.showErrorDialog(
                    this.simultaneousPaymentsErrorDialogTitle,
                    this.simultaneousPaymentsErrorMessage
                );
                this.$app.reloadWindow();
                return;
            }
            switch (pageInfo.ClientTypeName) {
                case "Pc2.Payments.StripeAdapter":
                    try {
                        const loadPromise = this.baseController.stripeLoadPromise || Promise.reject();
                        await loadPromise;
                    } catch (error) {
                        await this.$app.showErrorDialog(
                            this.stripeLoadingErrorDialogTitle,
                            this.stripeLoadingErrorMessage
                        );
                        this.$app.reloadWindow();
                        return;
                    }
                    this.adapter = new StripeAdapter();
                    break;
                case "Pc2.Payments.PcxAdapter":
                    this.adapter = new PcxAdapter();
                    break;
                case "Pc2.Payments.EzPayAdapter":
                    this.adapter = new EzPayAdapter();
                    break;
                case "Pc2.Payments.PaymentusAdapter":
                    this.adapter = new PaymentusAdapter();
                    break;
                default:
                    break;
            }

            this.pageInfo = pageInfo;
            if (this.adapter && this.adapter.delayedSubmit && this.projectLockStore.lockedForPendingPayment) {
                this.adapter.delayedSubmit(this);
                return;
            }
            if (
                this.projectLockStore.lockedForPendingPayment &&
                pageInfo.CurrentPendingChargeIsSuccessful &&
                pageInfo.FinishChargeToken
            ) {
                await this.finish(pageInfo.FinishChargeToken);
                return;
            }
            if (
                this.projectLockStore.lockedForPendingPayment &&
                pageInfo.ClientTypeName === "Pc2.Payments.StripeAdapter" &&
                this.adapter &&
                this.adapter.isStripeRedirectAttempt()
            ) {
                await this.adapter.processStripeUrlRedirect(this);
                return;
            }
            this.projectForm.setValidationErrors(this.pageInfo.FormValidationErrors);

            if (
                !this.projectLockStore.lockedForPendingPayment &&
                this.pageInfo.UnsubmittedPayments.length <= 0 &&
                !this.hasErrors
            ) {
                if (this.pageInfo.ShowPayment) {
                    if (this.pageInfo.PaymentInfo) {
                        await this.$nextTick();
                        this.adapter.init(this.pageInfo.PaymentInfo.AdapterData, this);
                    }
                    if (this.pageInfo.CaptureBillingEmail && !this.pageInfo.PaymentInfo.Email) {
                        this.showConfirmEmail = true;
                    }
                } else {
                    this.projectForm.showSubmitOnEPaymentPage = true;
                }
            }
            if (!this.projectLockStore.lockedForPendingPayment && this.hasUnsubmittedPayments) {
                this.projectForm.showSubmitOnEPaymentPage = true;
            }
        },
        methods: {
            _doEmailsMatch(confirmEmailProposedValue) {
                if (!this.pageInfo) {
                    return false;
                }
                const billingEmailLower = this.pageInfo.PaymentInfo.Email || "";
                const confirmBillingEmailLower = confirmEmailProposedValue || "";

                return billingEmailLower.trim().toLowerCase() === confirmBillingEmailLower.trim().toLowerCase();
            },
            hasErrorType(errors, errorType) {
                const errorsOfType = errors.filter((error) => error.Type === errorType);
                return errorsOfType.length > 0;
            },
            async finalizePaymentDetails() {
                if (this.adapter) {
                    // Stripe uses this method if a surcharge is being added or the
                    //  payment method has been chosen in the PowerClerk UI
                    if (this.adapter.updatePaymentAmountAndMethod) {
                        if (
                            this.pageInfo.IncludeTransactionFee ||
                            !this.mustSelectEPaymentType ||
                            this.selectedEPaymentType
                        ) {
                            let surchargeAmount = 0;
                            let totalAmount = 0;
                            if (this.pageInfo.IncludeTransactionFee) {
                                totalAmount = this.pageInfo.PaymentInfo.AmountInCents;
                                let selectedOption = null;
                                if (this.mustSelectEPaymentType && this.selectedEPaymentType) {
                                    selectedOption = this.ePaymentTypeOptions.find(
                                        (option) => option.ProviderMethodId === this.selectedEPaymentType
                                    );
                                } else if (this.ePaymentTypeOptions.length > 0) {
                                    selectedOption = this.ePaymentTypeOptions[0];
                                }
                                if (selectedOption) {
                                    surchargeAmount = selectedOption.SurchargeAmountInCents;
                                    totalAmount += surchargeAmount;
                                }
                            }
                            let paymentMethod = "";
                            if (this.pageInfo.MustSelectPaymentType) {
                                paymentMethod = this.selectedEPaymentType;
                            } else {
                                paymentMethod = this.ePaymentTypeOptions.map((o) => o.ProviderMethodId).join(",");
                            }
                            await this.adapter.updatePaymentAmountAndMethod(
                                surchargeAmount,
                                totalAmount,
                                paymentMethod
                            );
                        }
                    }
                    // Paymentus uses this method
                    if (this.adapter.updateEPaymentAuthToken) {
                        await this.adapter.updateEPaymentAuthToken(
                            this.pageInfo.PaymentInfo.Email,
                            this.selectedEPaymentType
                        );
                    }
                }
            },
            async processEPayment() {
                if (this.loading || !this.ePayIsOk) {
                    return;
                }
                let message = this.defaultProcessingMessage;
                if (this.adapter.processingMessage) {
                    message = this.adapter.processingMessage;
                }
                this.$app.blockUi(message);
                this.projectLockStore.setLockedForPendingPayment(true);
                this.$app.setUnsavedChanges(true);

                await this.finalizePaymentDetails();

                await this.$serviceControllers.EPayments.SetChargeToProcessing({
                    programId: this.programId,
                    projectId: this.projectId,
                    formVersionId: this.formVersionId,
                    chargeToken: {
                        ChargeId: this.chargeId,
                        Email: null,
                        AdapterData: []
                    }
                });

                const checkoutResult = await this.thirdPartyCheckout();
                if (checkoutResult.success) {
                    const chargeToken = {
                        ChargeId: this.chargeId,
                        Email: this.pageInfo.PaymentInfo.Email,
                        AdapterData: checkoutResult.adapterData
                    };
                    this.finish(chargeToken);
                } else {
                    this.cancel();
                }
            },

            async processManualPayment() {
                if (this.loading || !this.manualPayIsOk) {
                    return;
                }
                let message = this.defaultProcessingMessage;
                if (this.adapter.processingMessage) {
                    message = this.adapter.processingMessage;
                }
                this.$app.blockUi(message);

                try {
                    const result = await this.$serviceControllers.EPayments.FinishManualPayment({
                        programId: this.programId,
                        projectId: this.projectId,
                        formVersionId: this.formVersionId,
                        chargeToken: {
                            ChargeId: this.chargeId,
                            Email: this.pageInfo.PaymentInfo.Email,
                            AdapterData: []
                        }
                    });
                    const receiptData = result.Data.ReceiptInfo;
                    receiptData.IsManualPayment = true;
                    const navigateUrl = result.Data.NavigateUrl;
                    const submitResult = result.Data.SubmitResult;
                    this.$app.unBlockUi();
                    this.showDialogAndNavigateToNext(receiptData, navigateUrl, submitResult);
                } catch (e) {
                    this.cancel();
                }
            },
            thirdPartyCheckout() {
                let chargeDisplayAmount = this.pageInfo.PaymentInfo.DisplayAmount;
                if (this.pageInfo.IncludeTransactionFee) {
                    const selectedOption = this.ePaymentTypeOptions.find(
                        (option) => option.ProviderMethodId === this.selectedEPaymentType
                    );
                    if (selectedOption) {
                        chargeDisplayAmount = selectedOption.TotalDisplayAmount;
                    } else if (this.ePaymentTypeOptions.length > 0) {
                        chargeDisplayAmount = this.ePaymentTypeOptions[0].TotalDisplayAmount;
                    }
                }
                return this.adapter.checkout({
                    amount: this.pageInfo.PaymentInfo.AmountInCents,
                    displayAmount: chargeDisplayAmount,
                    ePaymentType: this.selectedEPaymentType,
                    email: this.pageInfo.PaymentInfo.Email,
                    name: this.pageInfo.PaymentInfo.PayerName,
                    agencyName: this.pageInfo.AgencyName,
                    app: this.$app,
                    chargeId: this.chargeId
                });
            },
            billingEmailValidated(validationResult) {
                this.emailValidationResults = validationResult;
                if (this.$refs.confirmEmail) {
                    this.$refs.confirmEmail.validate();
                }
            },
            confirmBillingEmailValidated(validationResult) {
                this.confirmEmailValidationResults = validationResult;
            },
            async finish(chargeToken, skipUnlock) {
                try {
                    if (!skipUnlock) {
                        this.projectLockStore.setLockedForPendingPayment(false);
                    }

                    const result = await this.$serviceControllers.EPayments.FinishCheckout({
                        programId: this.programId,
                        projectId: this.projectId,
                        formVersionId: this.formVersionId,
                        chargeToken
                    });
                    const receiptData = result.Data.ReceiptInfo;
                    const navigateUrl = result.Data.NavigateUrl;
                    const submitResult = result.Data.SubmitResult;
                    this.$app.setUnsavedChanges(false);
                    this.$app.unBlockUi();
                    this.showDialogAndNavigateToNext(receiptData, navigateUrl, submitResult);
                } catch (e) {
                    this.cancel();
                }
            },
            async showDialogAndNavigateToNext(receiptData, navigateUrl, submitResult) {
                let title = receiptData.IsManualPayment
                    ? this.outsidePaymentSuccessfulDialogTitle
                    : this.ePaymentSuccessfulDialogTitle;
                if (this.pageInfo.ReceiptDialogOverrideTitle) {
                    title = this.pageInfo.ReceiptDialogOverrideTitle;
                }
                const CONTINUE = "continue";
                const dialogResult = await this.$app.showDialog({
                    title: title,
                    customComponent: paymentReceipt,
                    customComponentOptions: {
                        receiptInfo: receiptData,
                        hideReceiptInfo: this.pageInfo.HidePaymentDetailsInReceipt
                    },
                    large: true,
                    buttons: [
                        {
                            text: this.successfulPaymentDialogButtonLabel,
                            primary: true,
                            result: CONTINUE,
                            testRole: "payment-continue-btn"
                        }
                    ],
                    testRole: "PaymentReceiptDialog"
                });
                if (dialogResult === CONTINUE) {
                    this.$app.setUnsavedChanges(false);
                    if (this.$app.setHref) {
                        this.$app.setHref(navigateUrl);
                    } else {
                        this.projectForm.emitSubmitSuccess({
                            ProjectNumber: receiptData.ProjectNumber,
                            CustomConfirmationMessage: submitResult.CustomConfirmationMessage
                        });
                    }
                }
            },
            async cancel() {
                this.$app.setUnsavedChanges(false);
                this.$app.unBlockUi();
                await this.cancelPending(false);
                this.projectForm.previousPage();
            },
            async cancelPending(reload) {
                if (this.adapter && this.adapter.cancelDelayedSubmit) {
                    this.adapter.cancelDelayedSubmit();
                }
                this.$app.blockUi(this.defaultProcessingMessage);
                const result = await this.$serviceControllers.EPayments.CancelPendingPayments({
                    programId: this.programId,
                    projectId: this.projectId
                });
                if (result.Data.UnlockedSuccessfully) {
                    this.projectLockStore.setLockedForPendingPayment(false);
                } else {
                    await this.$app.showDialog({
                        title: this.cancelUnsuccessfulDialogTitle,
                        message: this.cancelUnsuccessfulErrorMessage,
                        buttons: [
                            {
                                text: this.cancelUnsuccessfulDialogButtonLabel
                            }
                        ]
                    });
                }
                if (result.status === "success") {
                    this.$app.unBlockUi();
                    if (reload) {
                        this.$app.reloadWindow();
                    }
                }
            },
            async confirmCancelPending() {
                const NO = "no";
                const YES = "yes";
                const dialogResult = await this.$app.showDialog({
                    title: this.confirmCancelPaymentDialogTitle,
                    message: this.pageInfo.PaymentPageLockedMessageDialog,
                    buttons: [
                        {
                            text: this.noButtonLabel,
                            primary: true,
                            result: NO
                        },
                        {
                            text: this.yesButtonLabel,
                            result: YES
                        }
                    ]
                });
                if (dialogResult === YES) {
                    this.cancel();
                }
            },
            PayConnexionCheckChargeStatus() {
                return this.$serviceControllers.EPayments.PayConnexionCheckChargeStatus({
                    programId: this.programId,
                    projectId: this.projectId,
                    formVersionId: this.formVersionId,
                    chargeToken: {
                        ChargeId: this.chargeId,
                        Email: null,
                        AdapterData: []
                    }
                });
            },
            EzPayCheckChargeStatus() {
                return this.$serviceControllers.EPayments.EzPayCheckChargeStatus({
                    programId: this.programId,
                    projectId: this.projectId,
                    formVersionId: this.formVersionId,
                    chargeToken: {
                        ChargeId: this.chargeId,
                        Email: null,
                        AdapterData: []
                    }
                });
            },
            PaymentusUpdateEPaymentAuthToken(billingEmail, selectedEPaymentType) {
                return this.$serviceControllers.EPayments.PaymentusUpdateEPaymentAuthToken({
                    programId: this.programId,
                    projectId: this.projectId,
                    chargeId: this.chargeId,
                    email: billingEmail,
                    ePaymentType: selectedEPaymentType
                });
            },
            PaymentusCheckChargeStatus(billingEmail, paymentIsProcessing) {
                return this.$serviceControllers.EPayments.PaymentusCheckChargeStatus({
                    programId: this.programId,
                    projectId: this.projectId,
                    chargeToken: {
                        ChargeId: this.chargeId,
                        Email: billingEmail,
                        AdapterData: []
                    },
                    paymentIsProcessing: paymentIsProcessing
                });
            },
            StripeUpdatePaymentAmountAndMethod(surchargeAmount, totalAmount, paymentMethod) {
                return this.$serviceControllers.EPayments.StripeUpdatePaymentAmountAndMethod({
                    programId: this.programId,
                    projectId: this.projectId,
                    chargeId: this.chargeId,
                    surchargeAmount: surchargeAmount,
                    updatedAmount: totalAmount,
                    updatedPaymentMethod: paymentMethod
                });
            }
        }
    };
</script>
<style lang="scss" scoped>
    .epayment-loader-size {
        width: 2rem;
        height: 2rem;
    }
    .payment-button {
        height: 32px;
    }
</style>
